import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Building2, CreditCard, MailWarning, Receipt, RefreshCw, UserPlus, Users } from 'lucide-react';
import { cancelSubscription, getOrganization, getSubscription, listInvoices, listMembers } from '@/lib/api/admin';
import { ApiError, describeError } from '@/lib/api/client';
import type { AdminHotel, AdminUser, Member, SaasInvoice } from '@/lib/api/types';
import { formatDate, formatDateTime, formatMoney, formatNumber, plural } from '@/lib/format';
import { useUrlParam } from '@/lib/hooks';
import { AccessOrError } from '@/components/ui/access';
import { Badge, IdChip, type Tone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, Meter, Prop, Stat } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Notice, QueryError, Skeleton } from '@/components/ui/feedback';
import { PageHeader } from '@/components/ui/page';
import { Tabs } from '@/components/ui/tabs';
import { ROLE_LABEL } from '@/features/users/roles';
import { LIMIT_KEYS, LIMIT_LABEL } from '@/features/plans/plan-form';
import { lastPaidDay } from '@/features/subscriptions/assign';
import { AssignDialog, invalidateSubscription, METHOD_LABEL } from '@/features/subscriptions/assign-dialog';
import { orgLabel } from './derive';
import { useOrganizations } from './use-organizations';

const STATUS_LABEL: Record<string, string> = { trialing: 'Пробный период', active: 'Активна', past_due: 'Просрочена', suspended: 'Приостановлена', cancelled: 'Отменена' };
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral' | 'accent'> = { trialing: 'accent', active: 'ok', past_due: 'warn', suspended: 'danger', cancelled: 'neutral' };

/**
 * One organization: what we can derive (hotels, people) and what the
 * organization endpoints say if they let a super admin in — those are
 * "any member" routes, and whether the platform admin counts is the
 * backend's decision. A 403 is shown as exactly that, not as an error.
 *
 * Limits are read from `effectivePlan`, never `plan`: `plan` is what the
 * organization subscribed to, `effectivePlan` is what is enforced right now.
 */
export function OrganizationPage() {
  const { id = '' } = useParams();
  const { byId, isPending } = useOrganizations();
  const org = byId.get(id) ?? null;
  const [tab, setTab] = useUrlParam('tab', 'hotels');

  const profile = useQuery({ queryKey: ['organization', id], queryFn: () => getOrganization(id), enabled: !!id, retry: false });
  const subscription = useQuery({ queryKey: ['subscription', id], queryFn: () => getSubscription(id), enabled: !!id, retry: false });

  const qc = useQueryClient();
  const [assigning, setAssigning] = useState(false);
  const [confirmFree, setConfirmFree] = useState(false);
  const toFree = useMutation({
    mutationFn: () => cancelSubscription(id),
    onSuccess: () => {
      toast('success', 'Организация переведена на Free');
      invalidateSubscription(qc, id);
      setConfirmFree(false);
    },
    onError: (err) => toast('error', 'Не удалось перевести на Free', describeError(err)),
  });

  const missing = (err: unknown) => ApiError.is(err) && err.statusCode === 404;
  const forbidden = (err: unknown) => ApiError.is(err) && err.statusCode === 403;
  const name = profile.data?.name ?? (org ? orgLabel(org) : `Организация ${id.slice(0, 8)}`);

  return (
    <>
      <Link to="/organizations" className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-muted hover:text-accent-ink"><ArrowLeft size={13} />Все организации</Link>
      <PageHeader
        title={name}
        subtitle={<span className="flex flex-wrap items-center gap-2"><IdChip id={id} />{profile.data && <Badge tone={profile.data.isActive ? 'ok' : 'neutral'} dot>{profile.data.isActive ? 'Активна' : 'Неактивна'}</Badge>}{(profile.data?.effectivePlan ?? org?.plan) && <Badge mono>{profile.data?.effectivePlan ?? org?.plan}</Badge>}</span>}
      />

      {isPending ? <div className="flex gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[132px] flex-1" />)}</div> : org ? (
        <div className="mb-6 flex flex-wrap gap-3">
          <Stat label="Отели" value={formatNumber(org.hotels.length)} hint={`${org.activeHotels} активных`} icon={<Building2 size={18} />} tone="ok" />
          <Stat label="Номера" value={formatNumber(org.rooms)} icon={<Building2 size={18} />} tone="neutral" />
          <Stat label="Сотрудники" value={formatNumber(org.users.length)} icon={<Users size={18} />} tone="warn" />
          <Stat label="Каналы" value={formatNumber(org.channels)} hint={org.syncErrors ? `${plural(org.syncErrors, ['ошибка', 'ошибки', 'ошибок'])} синка` : undefined} icon={<RefreshCw size={18} />} tone={org.syncErrors ? 'danger' : 'accent'} />
        </div>
      ) : (
        <Notice tone="warn" className="mb-6" title="В списках отелей и сотрудников такой организации нет">Возможно, id указан с ошибкой, или у организации ещё нет ни отеля, ни сотрудника.</Notice>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader icon={<CreditCard size={18} />} title="Подписка" subtitle="Лимиты — по действующему тарифу (effectivePlan), а не по выбранному." />
          <div className="mb-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setAssigning(true)} disabled={subscription.isPending || (subscription.isError && !missing(subscription.error))}>Назначить / продлить</Button>
            {subscription.data && subscription.data.status !== 'cancelled' && (
              <Button size="sm" variant="danger" onClick={() => setConfirmFree(true)}>Перевести на Free</Button>
            )}
          </div>
          {subscription.isPending ? <Skeleton className="h-40" /> : subscription.isError ? (
            missing(subscription.error)
              ? <Notice tone="neutral" title="Подписки нет">Организация работает на Free. Назначьте тариф кнопкой «Назначить / продлить».</Notice>
              : forbidden(subscription.error)
              ? <Notice tone="neutral" title="Бэкенд не отдаёт подписку администратору платформы">Маршрут `/organizations/{'{id}'}/billing/subscription` открыт владельцу и администратору организации. Для этого экрана нужен админский эндпоинт — попросить бэкенд-команду.</Notice>
              : <QueryError error={subscription.error} onRetry={() => subscription.refetch()} />
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[subscription.data.status] ?? 'neutral'} dot>{STATUS_LABEL[subscription.data.status] ?? subscription.data.status}</Badge>
                <Badge mono>{subscription.data.effectivePlan.code}</Badge>
                {subscription.data.plan.code !== subscription.data.effectivePlan.code && <Badge tone="warn">выбран {subscription.data.plan.code}, действует {subscription.data.effectivePlan.code}</Badge>}
                {subscription.data.cancelAtPeriodEnd && <Badge tone="warn">отмена в конце периода</Badge>}
              </div>
              <div className="space-y-3">
                {LIMIT_KEYS.map((k) => <Meter key={k} label={LIMIT_LABEL[k]} value={subscription.data.usage[k]} max={subscription.data.effectivePlan.limits[k]} />)}
              </div>
              <dl className="mt-4 divide-y divide-line">
{subscription.data.provider === 'manual'
                  ? <Prop label="Оплачено вручную">{subscription.data.lockedAmount !== null ? formatMoney(subscription.data.lockedAmount, subscription.data.lockedCurrency ?? subscription.data.plan.currency) : 'без записи оплаты'}</Prop>
                  :                 <Prop label="Платит">{subscription.data.lockedAmount !== null ? `${formatMoney(subscription.data.lockedAmount, subscription.data.lockedCurrency ?? subscription.data.plan.currency)} / ${subscription.data.billingCycle === 'yearly' ? 'год' : 'мес'}` : '—'}</Prop>}
                {subscription.data.provider === 'manual'
                  ? <Prop label="Действует по">{subscription.data.currentPeriodEnd ? formatDate(lastPaidDay(subscription.data.currentPeriodEnd)) : '—'}</Prop>
                  : <Prop label="Оплачено до">{subscription.data.currentPeriodEnd ? formatDate(subscription.data.currentPeriodEnd) : '—'}</Prop>}
                {subscription.data.trialEndsAt && <Prop label="Пробный период до">{formatDate(subscription.data.trialEndsAt)}</Prop>}
                {subscription.data.graceUntil && (subscription.data.provider === 'manual'
                  ? <Prop label="Льгота по">{formatDate(lastPaidDay(subscription.data.graceUntil))}</Prop>
                  : <Prop label="Льготный период до">{formatDate(subscription.data.graceUntil)}</Prop>)}
                <Prop label="Право доступа">{subscription.data.isEntitled ? 'есть' : 'истекло'}{subscription.data.entitledUntil ? ` · до ${formatDate(subscription.data.entitledUntil)}` : ''}</Prop>
                {subscription.data.lastRenewalError && <Prop label="Ошибка продления"><span className="text-danger">{subscription.data.lastRenewalError}</span></Prop>}
                <Prop label="Провайдер оплаты">{subscription.data.provider === 'manual' ? 'вручную (админка)' : subscription.data.provider ?? '—'}</Prop>
              </dl>
            </>
          )}
          {assigning && (
            <AssignDialog
              organizationId={id}
              subscription={subscription.data ?? null}
              onClose={() => setAssigning(false)}
            />
          )}
          <Dialog
            open={confirmFree}
            onClose={() => setConfirmFree(false)}
            title="Перевести на Free?"
            footer={(
              <>
                <Button variant="ghost" onClick={() => setConfirmFree(false)}>Отмена</Button>
                <Button variant="danger" loading={toFree.isPending} onClick={() => toFree.mutate()}>Перевести на Free</Button>
              </>
            )}
          >
            <p className="px-5 py-4 text-sm leading-6">Организация сразу, без льготы, перейдёт на Free. Вернуть тариф можно кнопкой «Назначить / продлить».</p>
          </Dialog>
        </Card>

        <Card>
          <CardHeader title="Профиль" subtitle="Реквизиты организации из её собственного профиля." />
          {profile.isPending ? <Skeleton className="h-40" /> : profile.isError ? (
            forbidden(profile.error)
              ? <Notice tone="neutral" title="Профиль недоступен администратору платформы">Маршрут `/organizations/{'{id}'}` открыт участникам организации. Ниже — то, что известно по отелям и сотрудникам.</Notice>
              : <QueryError error={profile.error} onRetry={() => profile.refetch()} />
          ) : (
            <dl className="divide-y divide-line">
              <Prop label="Название">{profile.data.name}</Prop>
              <Prop label="Slug" mono>{profile.data.slug}</Prop>
              <Prop label="E-mail">{profile.data.email}</Prop>
              <Prop label="Телефон">{profile.data.phone ?? '—'}</Prop>
              <Prop label="Сайт">{profile.data.website ?? '—'}</Prop>
              <Prop label="Страна · валюта · язык">{profile.data.country} · {profile.data.currency} · {profile.data.language}</Prop>
              <Prop label="Часовой пояс">{profile.data.timezone}</Prop>
              <Prop label="Владелец" mono><IdChip id={profile.data.ownerId} /></Prop>
              <Prop label="Создана">{formatDateTime(profile.data.createdAt)}</Prop>
            </dl>
          )}
          {org?.owner && (
            <dl className={profile.isError ? 'divide-y divide-line' : 'mt-2 border-t border-line'}>
              <Prop label="Владелец (по членству)"><Link to={`/users?id=${org.owner.id}`} className="hover:text-accent-ink hover:underline">{org.owner.displayName || org.owner.email}</Link></Prop>
            </dl>
          )}
        </Card>

      </div>

      <Tabs
        className="mb-4 mt-6"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'hotels', label: 'Отели', icon: <Building2 size={15} />, count: org?.hotels.length },
          { value: 'users', label: 'Сотрудники', icon: <Users size={15} />, count: org?.users.length },
          { value: 'members', label: 'Членства и приглашения', icon: <UserPlus size={15} /> },
          { value: 'invoices', label: 'Счета', icon: <Receipt size={15} /> },
        ]}
      />
      {tab === 'hotels' && <HotelsTable hotels={org?.hotels ?? []} />}
      {tab === 'users' && <UsersTable users={org?.users ?? []} />}
      {tab === 'members' && <MembersTable organizationId={id} />}
      {tab === 'invoices' && <InvoicesTable organizationId={id} />}
    </>
  );
}

function HotelsTable({ hotels }: { hotels: AdminHotel[] }) {
  const navigate = useNavigate();
  const columns: Column<AdminHotel>[] = [
    { key: 'name', header: 'Отель', sortValue: (h) => h.name, render: (h) => <><div className="font-semibold text-ink">{h.name}</div><div className="text-xs text-muted">{h.city}</div>{h.syncError && <div className="line-clamp-1 text-xs text-danger">{h.syncError}</div>}</> },
    { key: 'rooms', header: 'Номера', align: 'right', sortValue: (h) => h.totalRooms, render: (h) => formatNumber(h.totalRooms) },
    { key: 'channels', header: 'Каналы', align: 'right', sortValue: (h) => h.connectedChannels, render: (h) => h.connectedChannels > 0 ? formatNumber(h.connectedChannels) : <span className="text-faint">0</span> },
    { key: 'status', header: 'Статус', align: 'center', sortValue: (h) => h.isActive, render: (h) => <Badge tone={h.isActive ? 'ok' : 'neutral'} dot>{h.isActive ? 'Активен' : 'Выключен'}</Badge> },
    { key: 'createdAt', header: 'Создан', sortValue: (h) => h.createdAt, hideBelow: 'md', className: 'text-xs text-muted whitespace-nowrap', render: (h) => formatDate(h.createdAt) },
  ];
  return <DataTable id="h_" rows={hotels} columns={columns} rowKey={(h) => h.id} defaultSort={{ key: 'name', dir: 'asc' }} onRowClick={(h) => navigate(`/hotels/${h.id}`)} empty={{ icon: <Building2 size={28} />, title: 'Отелей нет' }} />;
}

function UsersTable({ users }: { users: AdminUser[] }) {
  const navigate = useNavigate();
  const columns: Column<AdminUser>[] = [
    { key: 'name', header: 'Пользователь', sortValue: (u) => u.displayName || u.email, render: (u) => <><div className="font-semibold text-ink">{u.displayName || u.email}</div><div className="text-xs text-muted">{u.email}</div></> },
    { key: 'role', header: 'Роль', sortValue: (u) => u.role, render: (u) => u.role ? <Badge tone={u.role === 'owner' ? 'accent' : 'neutral'}>{ROLE_LABEL[u.role] ?? u.role}</Badge> : '—' },
    { key: 'verified', header: 'E-mail', align: 'center', sortValue: (u) => u.emailVerified, render: (u) => u.emailVerified ? <Badge tone="ok" dot>подтверждён</Badge> : <Badge tone="warn" dot>не подтверждён</Badge> },
    { key: 'createdAt', header: 'Создан', sortValue: (u) => u.createdAt, hideBelow: 'md', className: 'text-xs text-muted whitespace-nowrap', render: (u) => formatDate(u.createdAt) },
  ];
  return <DataTable id="u_" rows={users} columns={columns} rowKey={(u) => u.id} defaultSort={{ key: 'name', dir: 'asc' }} onRowClick={(u) => navigate(`/users?id=${u.id}`)} empty={{ icon: <Users size={28} />, title: 'Сотрудников нет' }} />;
}

const MAIL_TONE: Record<string, Tone> = { queued: 'neutral', sent: 'ok', failed: 'danger' };

/** Members as the organization itself sees them — active ones and pending invitations, with whether the invite e-mail went out. */
function MembersTable({ organizationId }: { organizationId: string }) {
  const members = useQuery({ queryKey: ['organization', organizationId, 'members'], queryFn: () => listMembers(organizationId), retry: false });
  if (members.isError) return <AccessOrError error={members.error} what="Членства" route="/organizations/{id}/members" onRetry={() => members.refetch()} />;
  const columns: Column<Member>[] = [
    { key: 'name', header: 'Участник', sortValue: (m) => m.displayName || m.email, render: (m) => <><div className="font-semibold text-ink">{m.displayName || m.email}</div><div className="text-xs text-muted">{m.email}{m.phone ? ` · ${m.phone}` : ''}</div></> },
    { key: 'role', header: 'Роль', sortValue: (m) => m.role, render: (m) => <Badge tone={m.role === 'owner' ? 'accent' : 'neutral'}>{ROLE_LABEL[m.role] ?? m.role}</Badge> },
    { key: 'scope', header: 'Доступ', sortValue: (m) => m.hotelId, hideBelow: 'md', render: (m) => m.hotelId ? <Link to={`/hotels/${m.hotelId}`} className="hover:underline"><IdChip id={m.hotelId} /></Link> : <span className="text-xs text-muted">вся организация</span> },
    { key: 'status', header: 'Статус', align: 'center', sortValue: (m) => `${m.inviteStatus}-${m.isActive}`, render: (m) => m.inviteStatus === 'pending' ? <Badge tone="warn" dot>приглашение</Badge> : m.isActive ? <Badge tone="ok" dot>активен</Badge> : <Badge tone="neutral" dot>деактивирован</Badge> },
    { key: 'mail', header: 'Письмо', hideBelow: 'lg', sortValue: (m) => m.inviteEmail?.status ?? null, render: (m) => m.inviteEmail ? <span className="flex items-center gap-1.5 text-xs"><Badge tone={MAIL_TONE[m.inviteEmail.status] ?? 'neutral'}>{m.inviteEmail.status}</Badge>{m.inviteEmail.error && <span className="text-danger" title={m.inviteEmail.error}><MailWarning size={13} /></span>}</span> : <span className="text-faint">—</span> },
    { key: 'joinedAt', header: 'Присоединился', sortValue: (m) => m.joinedAt ?? m.invitedAt, hideBelow: 'md', className: 'text-xs text-muted whitespace-nowrap', render: (m) => m.joinedAt ? formatDate(m.joinedAt) : `приглашён ${formatDate(m.invitedAt)}` },
  ];
  return <DataTable id="m_" rows={members.data ?? []} columns={columns} rowKey={(m) => m.id} defaultSort={{ key: 'name', dir: 'asc' }} loading={members.isPending} empty={{ icon: <UserPlus size={28} />, title: 'Членств нет' }} />;
}

const INVOICE_TONE: Record<string, Tone> = { paid: 'ok', pending: 'warn', failed: 'danger', cancelled: 'neutral', refunded: 'neutral' };

/** SaaS invoices — what the organization was billed and whether it paid. */
function InvoicesTable({ organizationId }: { organizationId: string }) {
  const invoices = useQuery({ queryKey: ['organization', organizationId, 'invoices'], queryFn: () => listInvoices(organizationId), retry: false });
  if (invoices.isError) return <AccessOrError error={invoices.error} what="Счета" route="/organizations/{id}/billing/invoices" onRetry={() => invoices.refetch()} />;
  const columns: Column<SaasInvoice>[] = [
    { key: 'createdAt', header: 'Выставлен', sortValue: (i) => i.createdAt, className: 'text-xs whitespace-nowrap', render: (i) => formatDateTime(i.createdAt) },
    { key: 'amount', header: 'Сумма', align: 'right', sortValue: (i) => i.amount, render: (i) => <span className="font-semibold text-ink">{formatMoney(i.amount, i.currency)}</span> },
    { key: 'cycle', header: 'Период', sortValue: (i) => i.billingCycle, hideBelow: 'md', render: (i) => i.billingCycle === 'yearly' ? 'год' : i.billingCycle === 'monthly' ? 'месяц' : i.billingCycle ?? '—' },
    { key: 'status', header: 'Статус', align: 'center', sortValue: (i) => i.status, render: (i) => <Badge tone={INVOICE_TONE[i.status] ?? 'neutral'} dot>{i.status}</Badge> },
    {
      key: 'provider',
      header: 'Способ',
      sortValue: (i) => i.provider,
      hideBelow: 'md',
      render: (i) => i.provider === 'manual'
        ? <><span className="text-xs text-ink">Вручную · {i.paymentMethod ? METHOD_LABEL[i.paymentMethod] : '—'}</span>{i.note && <div className="line-clamp-2 text-xs text-muted">{i.note}</div>}</>
        : <span className="text-xs text-muted">{i.provider}{i.providerRef ? ` · ${i.providerRef}` : ''}</span>,
    },
    { key: 'paidAt', header: 'Оплачен', sortValue: (i) => i.paidAt, hideBelow: 'lg', className: 'text-xs text-muted whitespace-nowrap', render: (i) => i.paidAt ? formatDateTime(i.paidAt) : '—' },
  ];
  return <DataTable id="i_" rows={invoices.data ?? []} columns={columns} rowKey={(i) => i.id} defaultSort={{ key: 'createdAt', dir: 'desc' }} loading={invoices.isPending} empty={{ icon: <Receipt size={28} />, title: 'Счетов нет' }} />;
}
